import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Mail, RefreshCw, Send, AlertTriangle, X, CheckCircle, Users, MapPin } from 'lucide-react';
import MainService from '../../services/MainService';
import SearchableMultiSelect from '../ui/SearchableMultiSelect';

const DraftEmails = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [draft, setDraft] = useState({
    Subject: '',
    Message: '',
    SelectedClientIds: [],
    SelectedCustomerIds: [],
    IsAllClients: false,
    IsAllCustomers: false,
    IsGeography: false,
    SelectedAreasIds: [],
    IsAreaClient: true,
    IsAreaCustomer: true
  });

  const [clients, setClients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [areas, setAreas] = useState([]);

  // Dummy data for now
  useEffect(() => {
    // TODO: Replace with actual API calls when available
    // MainService.ClientsInAutoComplete().then(response => setClients(response));
    // MainService.CustomersInAutoComplete().then(response => setCustomers(response));
    // MainService.AreasInGrid().then(response => setAreas(response));
    
    // Dummy data
    setClients([
      { Id: 1, Name: 'Client 1' },
      { Id: 2, Name: 'Client 2' },
      { Id: 3, Name: 'Client 3' },
      { Id: 4, Name: 'Client 4' }
    ]);
    
    setCustomers([
      { Id: 1, Name: 'Customer 1' },
      { Id: 2, Name: 'Customer 2' },
      { Id: 3, Name: 'Customer 3' },
      { Id: 4, Name: 'Customer 4' }
    ]);
    
    setAreas([
      { Id: 1, Name: 'Dubai Marina' },
      { Id: 2, Name: 'Downtown Dubai' },
      { Id: 3, Name: 'JBR' },
      { Id: 4, Name: 'Business Bay' },
      { Id: 5, Name: 'Palm Jumeirah' }
    ]);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDraft(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMessageChange = (value) => {
    setDraft(prev => ({
      ...prev,
      Message: value
    }));
  };

  const handleClientIdsChange = (ids) => {
    setDraft(prev => ({
      ...prev,
      SelectedClientIds: ids
    }));
  };

  const handleCustomerIdsChange = (ids) => {
    setDraft(prev => ({
      ...prev,
      SelectedCustomerIds: ids
    }));
  };

  const handleAreasIdsChange = (ids) => {
    setDraft(prev => ({
      ...prev,
      SelectedAreasIds: ids
    }));
  };

  const handleReset = () => {
    setDraft({
      Subject: '',
      Message: '',
      SelectedClientIds: [],
      SelectedCustomerIds: [],
      IsAllClients: false,
      IsAllCustomers: false,
      IsGeography: false,
      SelectedAreasIds: [],
      IsAreaClient: true,
      IsAreaCustomer: true
    });
    setError('');
    setSuccess('');
  };

  const handleSendEmail = () => {
    // Validation
    if (!draft.Message || draft.Message.trim() === '' || draft.Message === '<p><br></p>') {
      setError('Enter Mail Content');
      return;
    }
    
    if (!draft.Subject || draft.Subject.trim() === '') {
      setError('Enter Subject');
      return;
    }
    
    if (!draft.IsAllClients && !draft.IsAllCustomers && 
        draft.SelectedClientIds.length === 0 && draft.SelectedCustomerIds.length === 0 &&
        !draft.IsGeography) {
      setError('Select Customer Or Clients');
      return;
    }
    
    if (draft.IsGeography && draft.SelectedAreasIds.length === 0) {
      setError('Select Areas');
      return;
    }
    
    setError('');
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // TODO: Replace with actual API call when available
      // const response = await MainService.DraftEmail(draft);
      // if (response) {
      //   setSuccess('Email sent successfully');
      //   setShowConfirmModal(false);
      //   handleReset();
      // } else {
      //   setError('Something went wrong');
      // }
      
      // Simulate API call
      setTimeout(() => {
        setSuccess('Email sent successfully');
        setShowConfirmModal(false);
        handleReset();
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Network Error');
      setLoading(false);
    }
  };

  // Quill editor modules
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Mail className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Draft Emails</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSendEmail}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              <span>Send Email</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">Compose and send emails to clients and customers</p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Email Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Content <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={draft.Message}
                onChange={handleMessageChange}
                modules={modules}
                formats={formats}
                placeholder="Draft Email Here"
                style={{ minHeight: '300px' }}
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="Subject"
              value={draft.Subject}
              onChange={handleInputChange}
              placeholder="Enter email subject"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Clients Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-11">
              <SearchableMultiSelect
                value={draft.SelectedClientIds}
                onChange={handleClientIdsChange}
                options={clients}
                placeholder="Select Clients..."
                disabled={draft.IsAllClients}
                label="Clients"
              />
            </div>
            <div className="lg:col-span-1 flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="IsAllClients"
                  checked={draft.IsAllClients}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                />
                <span className="text-sm text-gray-700">All</span>
              </label>
            </div>
          </div>

          {/* Customers Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-11">
              <SearchableMultiSelect
                value={draft.SelectedCustomerIds}
                onChange={handleCustomerIdsChange}
                options={customers}
                placeholder="Select Customers..."
                disabled={draft.IsAllCustomers}
                label="Customers"
              />
            </div>
            <div className="lg:col-span-1 flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="IsAllCustomers"
                  checked={draft.IsAllCustomers}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                />
                <span className="text-sm text-gray-700">All</span>
              </label>
            </div>
          </div>

          {/* Geography Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">By Geography</h3>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="IsGeography"
                  checked={draft.IsGeography}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                />
                <span className="text-sm text-gray-700">Enable</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 mb-4">Send SMS by areas</p>

            {draft.IsGeography && (
              <div className="space-y-4 pl-8 border-l-2 border-gray-200">
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="IsAreaClient"
                      checked={draft.IsAreaClient}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                    />
                    <span className="text-sm text-gray-700">Client Enquiries</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="IsAreaCustomer"
                      checked={draft.IsAreaCustomer}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                    />
                    <span className="text-sm text-gray-700">Customer Enquiries</span>
                  </label>
                </div>

                <div>
                  <SearchableMultiSelect
                    value={draft.SelectedAreasIds}
                    onChange={handleAreasIdsChange}
                    options={areas}
                    placeholder="Select Areas..."
                    label="Areas"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                <h3 className="text-lg font-semibold text-gray-900">Confirm to Send Email</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to send this email? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSend}
                  disabled={loading}
                  className="px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftEmails;

